/**
 * NEW SEND (design_handoff_club_pages/README.md §2 SEND): one panel to reach the page's people.
 *   TO   ALL MEMBERS (followers) · EVERYONE (members + the whole contact list) · <each group> · PICK PEOPLE
 *   AS   NOTIFICATION · EMAIL · TEXT
 * then the subject (email only), the message, the live `REACHES N OF M` line from the count
 * endpoint, a yellow HEADS UP when some of the audience can't be reached on that channel, and
 * SEND TO N (or PICK SOMEONE FIRST).
 *
 * NOTIFICATION goes out straight from here: save a broadcast with channel `notification` and the
 * message as `smsBody`, then send it (no test step — it is free and lands in the sender's own
 * bell). EMAIL and TEXT save a draft with the chosen audience and hand off to the builders, which
 * keep the preview, the test send, drafts and scheduling.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { audienceService } from '../../services';
import { useDialog } from '../../context/DialogContext';
import { ClubPanel, PanelHead, PillButton, RingTab, INK, MUTED, INSET, CHIP, label as lbl, textareaStyle, inputStyle } from '../club/clubUi';
import PickPeople from './PickPeople';

export const CHANNEL_OPTIONS = [['notification', 'NOTIFICATION'], ['email', 'EMAIL'], ['sms', 'TEXT']];
const CHANNEL_WORD = { notification: 'aren’t on Portal, so can’t get a notification', email: 'have no email', sms: 'have no phone number' };

/** The wire audience for a TO choice. */
export function audienceFor(to, picked) {
  if (to === 'followers') return { audienceKind: 'followers', audience: { userIds: [], contactIds: [], groupIds: [] } };
  if (to === 'everyone') return { audienceKind: 'everyone', audience: { userIds: [], contactIds: [], groupIds: [] } };
  if (to === 'contacts') return { audienceKind: 'contacts', audience: { userIds: [], contactIds: [], groupIds: [] } };
  if (to === 'people') return { audienceKind: 'people', audience: { userIds: picked.filter((p) => p.kind === 'user').map((p) => p.id), contactIds: picked.filter((p) => p.kind === 'contact').map((p) => p.id), groupIds: [] } };
  return { audienceKind: 'contacts', audience: { userIds: [], contactIds: [], groupIds: [to] } };
}

export default function NewSendPanel({ owner, perms = { canSend: true }, mobile = false, initialPick = null, onOpenBuilder, onSent, onToast }) {
  const { ownerType, ownerId, followerWord = 'MEMBERS' } = owner;
  const { showAlert } = useDialog();
  const [groups, setGroups] = useState([]);
  const [counts, setCounts] = useState({});
  const [pool, setPool] = useState(null);
  const [to, setTo] = useState(initialPick ? 'people' : 'followers');
  const [channel, setChannel] = useState('notification');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [picked, setPicked] = useState([]);
  const [reach, setReach] = useState(null);
  const [busy, setBusy] = useState(false);
  const client = useMemo(() => audienceService.broadcastClient(ownerType, ownerId), [ownerType, ownerId]);

  useEffect(() => {
    let live = true;
    audienceService.listGroups(ownerType, ownerId).then((r) => { if (live && r.success) setGroups(r.data || []); });
    audienceService.listContacts(ownerType, ownerId, { page: 1, pageSize: 1 }).then((r) => { if (live && r.success) setCounts(r.data?.counts || {}); });
    audienceService.searchPeople(ownerType, ownerId, '', 1000).then((r) => { if (live) setPool(r.success ? (r.data || []) : []); });
    return () => { live = false; };
  }, [ownerType, ownerId]);

  // SEND TO THESE → from the contact list: the ids arrive before the pool does; resolve once it lands.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !pool || !initialPick) return;
    seeded.current = true;
    const users = new Set(initialPick.userIds || []); const contacts = new Set(initialPick.contactIds || []);
    const hit = pool.filter((p) => (p.kind === 'user' && users.has(p.id)) || (p.kind === 'contact' && contacts.has(p.id)));
    if (hit.length) { setPicked(hit); setTo('people'); }
  }, [pool, initialPick]);

  const spec = useMemo(() => ({ channel, ...audienceFor(to, picked) }), [channel, to, picked]);
  const specKey = JSON.stringify(spec);
  useEffect(() => {
    let live = true;
    if (to === 'people' && picked.length === 0) { setReach({ count: 0, noAddress: 0, optedOut: 0 }); return undefined; }
    const t = setTimeout(async () => {
      const r = await audienceService.countAudience(ownerType, ownerId, JSON.parse(specKey));
      if (live) setReach(r.success ? r.data : null);
    }, 250);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerType, ownerId, specKey]);

  const total = reach ? reach.count + (reach.noAddress || 0) + (reach.optedOut || 0) : 0;
  const skip = reach ? (reach.noAddress || 0) : 0;
  const canSend = !!message.trim() && (channel !== 'email' || !!subject.trim()) && !!reach && reach.count > 0 && !busy && perms.canSend !== false;

  const audiences = [
    ['followers', `ALL ${followerWord}`, counts.followers],
    ['everyone', 'EVERYONE', null],
    ...groups.map((g) => [g.id, String(g.name).toUpperCase(), g.count]),
    ['people', 'PICK PEOPLE', picked.length],
  ];

  const doSend = useCallback(async () => {
    if (!canSend) return;
    setBusy(true);
    const base = { channel, ...audienceFor(to, picked) };
    const saved = await client.save(channel === 'email'
      ? { ...base, subject: subject.trim(), preview: message.trim().slice(0, 80), intro: message.trim(), sections: [] }
      : { ...base, smsBody: message.trim() });
    if (!saved?.success) { setBusy(false); await showAlert({ title: 'COULD NOT SAVE', message: saved?.error || saved?.message || 'Try again.', type: 'error' }); return; }
    const id = saved.data?.newsletter?.id || saved.data?.id;
    if (channel !== 'notification') { setBusy(false); onOpenBuilder?.(id, channel); return; }
    const sent = await client.send(id);
    setBusy(false);
    if (!sent?.success) { await showAlert({ title: 'COULD NOT SEND', message: sent?.error || sent?.message || 'Try again.', type: 'error' }); return; }
    onToast?.(`SENT TO ${reach.count}`);
    setMessage(''); setSubject('');
    onSent?.();
  }, [canSend, channel, to, picked, client, subject, message, reach, onOpenBuilder, onSent, onToast, showAlert]);

  const sendLabel = busy ? 'SENDING…' : to === 'people' && picked.length === 0 ? 'PICK SOMEONE FIRST' : channel === 'notification' ? `SEND TO ${reach?.count ?? 0}` : channel === 'email' ? `WRITE THE EMAIL · ${reach?.count ?? 0}` : `WRITE THE TEXT · ${reach?.count ?? 0}`;

  return (
    <ClubPanel phone={mobile} style={{ gap: 16 }} data-testid="new-send">
      <PanelHead title="NEW SEND" right={reach ? `REACHES ${reach.count} OF ${total}` : '…'} />
      <div className="flex flex-col" style={{ gap: 8 }}>
        <span style={lbl(10, '.16em')}>TO</span>
        <span className="flex flex-wrap" style={{ gap: 6 }} role="radiogroup" aria-label="Audience">
          {audiences.map(([k, l, n]) => (
            <RingTab key={k} on={to === k} off={CHIP} height={38} fontSize={10} tracking=".12em" onClick={() => setTo(k)} role="radio" aria-checked={to === k} data-testid={`to-${k}`}>
              {l}{n != null && <span style={{ opacity: 0.6 }}>{Number(n).toLocaleString()}</span>}
            </RingTab>
          ))}
        </span>
      </div>
      {to === 'people' && <PickPeople ownerType={ownerType} ownerId={ownerId} channel={channel} groups={groups} picked={picked} onChange={setPicked} followerWord={followerWord} pool={pool} />}
      {skip > 0 && (
        <span className="flex items-center" style={{ gap: 10, padding: '10px 16px', borderRadius: 16, background: '#FEF3C7', fontSize: 11, lineHeight: 1.5 }} data-testid="heads-up">
          <span style={{ ...lbl(9, '.14em', 800) }}>HEADS UP</span>{skip} of {total} {CHANNEL_WORD[channel]}. They’re skipped for this send.
        </span>
      )}
      <div className="flex flex-col" style={{ gap: 8 }}>
        <span style={lbl(10, '.16em')}>AS</span>
        <span className="inline-flex self-start" style={{ gap: 2, padding: 3, borderRadius: 999, background: INSET }} role="radiogroup" aria-label="Channel">
          {CHANNEL_OPTIONS.map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={channel === k} onClick={() => setChannel(k)} data-testid={`as-${k}`}
              style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 999, background: channel === k ? INK : 'transparent', color: channel === k ? '#fff' : INK, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer' }}>
              {l}
            </button>
          ))}
        </span>
      </div>
      {channel === 'email' && <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" aria-label="Subject" style={inputStyle} data-testid="send-subject" />}
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} maxLength={320} placeholder="What do you want to tell them?" aria-label="Message" style={textareaStyle} data-testid="send-message" />
      <span className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
        <span style={{ fontSize: 11, color: MUTED }}>{channel === 'notification' ? 'Lands in their bell and on their phone. Nothing is charged to send.' : channel === 'email' ? 'Next: the email builder, with this audience picked. Every email carries an unsubscribe link.' : 'Next: the text builder, with this audience picked. You get the test first.'}</span>
        <PillButton size={48} onClick={doSend} disabled={!canSend} style={{ opacity: canSend ? 1 : 0.35, cursor: canSend ? 'pointer' : 'default' }} data-testid="send-button">{sendLabel}</PillButton>
      </span>
    </ClubPanel>
  );
}
